import React, { useState, useEffect } from 'react';
import { creatorAPI } from '../../services/api';
import { Zap, Star, Radio, BarChart2, CheckCircle, Loader, AlertCircle, Plus, X } from 'lucide-react';

const PLAN_MONTHLY = { id: 'monthly', price: 29.90, label: 'Monthly', founding: true, period: '/month', anchor: 59.90 };
const PLAN_ANNUAL  = { id: 'annual',  price: 249.00, label: 'Annual',  founding: true, period: '/year',  anchor: 499.00, saving: 'Save ~$110' };

const BOOST_FEATURES = [
  { icon: Radio,    text: 'Carousel Rotation — your reaction videos appear in the platform carousel throughout the boost month' },
  { icon: Star,     text: 'Featured Spots — eligible for Reaction of the Week, community polls & discovery pools' },
  { icon: BarChart2, text: 'Priority Placement — boosted channels appear higher in poster-click reaction overlays' },
  { icon: Zap,      text: 'Analytics Insights — see how many impressions your boost generated each week' },
];

const extractVideoId = (url) => {
  if (!url) return null;
  const m = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([A-Za-z0-9_-]{11})/);
  return m ? m[1] : null;
};

const VideoSlot = ({ index, value, onChange, onRemove }) => {
  const videoId = extractVideoId(value.url);
  return (
    <div style={{
      display: 'flex', alignItems: 'flex-start', gap: '12px',
      background: 'rgba(255,255,255,0.03)', border: '1px solid #1E1E30',
      borderRadius: '8px', padding: '12px',
    }}>
      {videoId ? (
        <img
          src={`https://img.youtube.com/vi/${videoId}/mqdefault.jpg`}
          alt=""
          style={{ width: '80px', height: '45px', borderRadius: '4px', objectFit: 'cover', flexShrink: 0 }}
        />
      ) : (
        <div style={{
          width: 80, height: 45, borderRadius: 4,
          background: '#111', border: '1px dashed #333',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: '11px', color: '#555', flexShrink: 0,
        }}>Video {index + 1}</div>
      )}
      <div style={{ flex: 1, minWidth: 0 }}>
        <input
          type="url"
          placeholder="https://youtube.com/watch?v=..."
          value={value.url}
          onChange={e => onChange({ ...value, url: e.target.value })}
          style={{
            width: '100%', background: 'transparent', border: 'none',
            borderBottom: '1px solid #2A2A3E', color: '#E5E5E5',
            fontSize: '13px', padding: '4px 0', outline: 'none', boxSizing: 'border-box',
          }}
        />
        <input
          type="text"
          placeholder="Optional label (e.g. 'Movie Night Reaction')"
          value={value.title}
          onChange={e => onChange({ ...value, title: e.target.value })}
          style={{
            width: '100%', background: 'transparent', border: 'none',
            borderBottom: '1px solid #1A1A2E', color: '#888',
            fontSize: '12px', padding: '4px 0 0', outline: 'none', marginTop: '6px', boxSizing: 'border-box',
          }}
        />
      </div>
      <button onClick={onRemove} style={{
        background: 'none', border: 'none', cursor: 'pointer',
        color: '#555', padding: '2px', flexShrink: 0,
      }}><X size={16} /></button>
    </div>
  );
};

const ActiveBoostCard = ({ boost, onUpdateCarousel }) => {
  const [slots, setSlots] = useState(
    (boost.carouselVideos || []).map(v => ({ url: v.url || '', title: v.title || '' }))
  );
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const daysLeft = boost.endDate
    ? Math.max(0, Math.ceil((new Date(boost.endDate) - Date.now()) / 86400000))
    : null;

  const addSlot = () => {
    if (slots.length < 5) setSlots([...slots, { url: '', title: '' }]);
  };

  const saveCarousel = async () => {
    setSaving(true);
    try {
      await creatorAPI.updateBoostCarousel(slots.filter(s => s.url));
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
      if (onUpdateCarousel) onUpdateCarousel();
    } catch (e) {
      // noop
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      {/* Active badge */}
      <div style={{
        display: 'inline-flex', alignItems: 'center', gap: '6px',
        background: 'rgba(16,185,129,0.12)', border: '1px solid rgba(16,185,129,0.25)',
        borderRadius: '20px', padding: '5px 14px', marginBottom: '24px',
      }}>
        <CheckCircle size={13} style={{ color: '#10B981' }} />
        <span style={{ fontSize: '12px', color: '#10B981', fontWeight: '600' }}>DISCOVERY BOOST ACTIVE</span>
      </div>

      {/* Stats row */}
      <div style={{ display: 'flex', gap: '16px', marginBottom: '28px', flexWrap: 'wrap' }}>
        {[
          { label: 'Plan',               value: boost.billingPeriod === 'annual' ? 'Annual' : 'Monthly' },
          { label: 'Days Left',          value: daysLeft !== null ? daysLeft : '—' },
          { label: 'Carousel Videos',    value: (boost.carouselVideos || []).filter(v => v.url).length + ' / 5' },
          { label: 'Overlay Impressions', value: boost.overlayImpressions ?? 0 },
        ].map((s, i) => (
          <div key={i} style={{
            flex: '1 1 120px', background: 'rgba(139,92,246,0.08)',
            border: '1px solid rgba(139,92,246,0.2)', borderRadius: '10px',
            padding: '14px 18px',
          }}>
            <div style={{ fontSize: '11px', color: '#777', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{s.label}</div>
            <div style={{ fontSize: '22px', fontWeight: '700', color: '#C4B5FD' }}>{s.value}</div>
          </div>
        ))}
      </div>

      {/* Carousel editor */}
      <div style={{ marginBottom: '12px' }}>
        <div style={{ fontSize: '13px', fontWeight: '600', color: '#E5E5E5', marginBottom: '6px' }}>
          Carousel Video Slots
          <span style={{ fontSize: '11px', color: '#666', fontWeight: '400', marginLeft: '8px' }}>
            These reaction videos will rotate in the platform carousel during your boost month.
          </span>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginTop: '12px' }}>
          {slots.map((slot, i) => (
            <VideoSlot
              key={i}
              index={i}
              value={slot}
              onChange={v => { const s = [...slots]; s[i] = v; setSlots(s); }}
              onRemove={() => setSlots(slots.filter((_, j) => j !== i))}
            />
          ))}
        </div>
        <div style={{ display: 'flex', gap: '10px', marginTop: '12px' }}>
          {slots.length < 5 && (
            <button onClick={addSlot} style={{
              display: 'flex', alignItems: 'center', gap: '6px',
              background: 'rgba(139,92,246,0.1)', border: '1px solid rgba(139,92,246,0.25)',
              color: '#A78BFA', padding: '8px 14px', borderRadius: '7px',
              cursor: 'pointer', fontSize: '13px',
            }}>
              <Plus size={14} /> Add Video
            </button>
          )}
          <button onClick={saveCarousel} disabled={saving} style={{
            display: 'flex', alignItems: 'center', gap: '6px',
            background: saving ? 'rgba(139,92,246,0.1)' : 'linear-gradient(135deg,#8B5CF6,#6D28D9)',
            border: 'none', color: '#FFF', padding: '8px 18px', borderRadius: '7px',
            cursor: saving ? 'not-allowed' : 'pointer', fontSize: '13px', fontWeight: '600',
          }}>
            {saving ? <Loader size={14} style={{ animation: 'spin 1s linear infinite' }} /> : null}
            {saved ? '✓ Saved' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
};

const RequestForm = () => {
  const [plan, setPlan] = useState('monthly');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const selectedPlan = plan === 'annual' ? PLAN_ANNUAL : PLAN_MONTHLY;

  const subscribe = async () => {
    setSubmitting(true);
    setError('');
    try {
      const result = await creatorAPI.createCheckoutSession({ billingPeriod: plan });
      if (result.success && result.url) window.location.href = result.url;
    } catch (e) {
      setError(e.response?.data?.error || 'Something went wrong. Please try again.');
      setSubmitting(false);
    }
  };

  return (
    <div>
      {/* CTA Headlines */}
      <div style={{ textAlign: 'center', marginBottom: '40px' }}>
        <div style={{
          display: 'inline-flex', alignItems: 'center', gap: '6px',
          background: 'rgba(251,191,36,0.1)', border: '1px solid rgba(251,191,36,0.25)',
          borderRadius: '20px', padding: '5px 16px', marginBottom: '24px',
        }}>
          <Zap size={13} style={{ color: '#FBBF24' }} />
          <span style={{ fontSize: '11px', color: '#FBBF24', fontWeight: '700', letterSpacing: '1px' }}>EARLY ACCESS — FOUNDING PRICE</span>
        </div>
        <h2 style={{
          fontSize: '28px', fontWeight: '800', color: '#FFF',
          letterSpacing: '-0.5px', margin: '0 0 10px',
          background: 'linear-gradient(135deg, #E9D5FF, #A78BFA, #8B5CF6)',
          WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
        }}>MAKE YOUR CHANNEL KNOWN</h2>
        <div style={{ fontSize: '15px', color: '#A78BFA', fontWeight: '600', marginBottom: '8px', letterSpacing: '0.5px' }}>
          INCREASE DISCOVERY POTENTIAL
        </div>
        <div style={{ fontSize: '13px', color: '#8B5CF6', fontWeight: '600', letterSpacing: '1px' }}>
          AMPLIFY YOUR FANBASE
        </div>
        <p style={{ fontSize: '14px', color: '#666', maxWidth: '480px', margin: '14px auto 0', lineHeight: '1.6' }}>
          Get your channel and reaction videos in front of the ReactorHub community — through the platform carousel, featured spots, and discovery pools.
        </p>
      </div>

      {/* Feature list */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '12px', marginBottom: '36px' }}>
        {BOOST_FEATURES.map((f, i) => (
          <div key={i} style={{
            display: 'flex', alignItems: 'flex-start', gap: '12px',
            background: 'rgba(139,92,246,0.06)', border: '1px solid rgba(139,92,246,0.15)',
            borderRadius: '10px', padding: '14px 16px',
          }}>
            <f.icon size={18} style={{ color: '#8B5CF6', flexShrink: 0, marginTop: '1px' }} />
            <span style={{ fontSize: '13px', color: '#C4B5FD', lineHeight: '1.5' }}>{f.text}</span>
          </div>
        ))}
      </div>

      {/* Plan selector */}
      <div style={{ display: 'flex', gap: '12px', marginBottom: '28px', justifyContent: 'center' }}>
        {[PLAN_MONTHLY, PLAN_ANNUAL].map(p => (
          <button key={p.id} onClick={() => setPlan(p.id)} style={{
            flex: '1', maxWidth: '240px', padding: '18px 20px',
            borderRadius: '12px', cursor: 'pointer', textAlign: 'left',
            border: plan === p.id ? '2px solid #8B5CF6' : '2px solid #1E1E30',
            background: plan === p.id ? 'rgba(139,92,246,0.12)' : 'rgba(255,255,255,0.03)',
            transition: 'all 0.15s',
          }}>
            <div style={{ fontSize: '11px', color: '#888', fontWeight: '600', letterSpacing: '0.5px', marginBottom: '6px', textTransform: 'uppercase' }}>
              {p.label} {p.saving && <span style={{ color: '#10B981', marginLeft: '6px' }}>{p.saving}</span>}
            </div>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: '4px' }}>
              <span style={{ fontSize: '28px', fontWeight: '800', color: '#FFF' }}>${p.price.toFixed(2)}</span>
              <span style={{ fontSize: '13px', color: '#666' }}>{p.period}</span>
            </div>
            <div style={{ fontSize: '11px', color: '#555', marginTop: '4px', textDecoration: 'line-through' }}>
              Regular ${p.anchor.toFixed(2)}{p.period}
            </div>
            <div style={{ fontSize: '10px', color: '#FBBF24', marginTop: '6px', fontWeight: '600' }}>
              ★ Founding price — locked in for 24 months
            </div>
          </button>
        ))}
      </div>

      {error && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: '8px',
          background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.25)',
          borderRadius: '8px', padding: '10px 14px', marginBottom: '16px',
          color: '#F87171', fontSize: '13px',
        }}>
          <AlertCircle size={14} /> {error}
        </div>
      )}

      {/* Subscribe button */}
      <div style={{ textAlign: 'center' }}>
        <button onClick={subscribe} disabled={submitting} style={{
          display: 'inline-flex', alignItems: 'center', gap: '10px',
          background: submitting ? 'rgba(139,92,246,0.4)' : 'linear-gradient(135deg, #8B5CF6, #6D28D9)',
          border: 'none', color: '#FFF', padding: '16px 40px',
          borderRadius: '12px', cursor: submitting ? 'not-allowed' : 'pointer',
          fontSize: '16px', fontWeight: '700',
          boxShadow: submitting ? 'none' : '0 4px 24px rgba(139,92,246,0.4)',
          transition: 'all 0.15s',
        }}>
          {submitting
            ? <><Loader size={18} style={{ animation: 'spin 1s linear infinite' }} /> Redirecting to payment...</>
            : <><Zap size={18} /> Subscribe — {selectedPlan.label} ${selectedPlan.price.toFixed(2)}{selectedPlan.period}</>
          }
        </button>
        <div style={{ fontSize: '12px', color: '#555', marginTop: '10px' }}>
          Secure payment via Stripe · Cancel anytime
        </div>
      </div>
    </div>
  );
};

const RequestedCard = ({ boost }) => (
  <div style={{ textAlign: 'center', padding: '40px 24px' }}>
    <div style={{
      display: 'inline-flex', alignItems: 'center', gap: '6px',
      background: 'rgba(251,191,36,0.1)', border: '1px solid rgba(251,191,36,0.25)',
      borderRadius: '20px', padding: '6px 18px', marginBottom: '20px',
    }}>
      <Loader size={13} style={{ color: '#FBBF24', animation: 'spin 1s linear infinite' }} />
      <span style={{ fontSize: '12px', color: '#FBBF24', fontWeight: '600' }}>REQUEST PENDING REVIEW</span>
    </div>
    <h3 style={{ fontSize: '20px', color: '#FFF', fontWeight: '700', margin: '0 0 10px' }}>
      We've received your request!
    </h3>
    <p style={{ fontSize: '14px', color: '#666', maxWidth: '400px', margin: '0 auto', lineHeight: '1.6' }}>
      Our team will reach out to your account email to confirm payment and get your boost activated.
      Once active you'll see your carousel slots here and can update them any time.
    </p>
    <div style={{
      display: 'inline-flex', gap: '24px', marginTop: '28px',
      background: 'rgba(255,255,255,0.03)', border: '1px solid #1E1E30',
      borderRadius: '10px', padding: '16px 28px',
    }}>
      <div>
        <div style={{ fontSize: '11px', color: '#666', marginBottom: '3px' }}>Plan</div>
        <div style={{ fontSize: '14px', color: '#C4B5FD', fontWeight: '600' }}>
          {boost.billingPeriod === 'annual' ? 'Annual' : 'Monthly'} — ${boost.price?.toFixed(2)}/{boost.billingPeriod === 'annual' ? 'yr' : 'mo'}
        </div>
      </div>
      <div>
        <div style={{ fontSize: '11px', color: '#666', marginBottom: '3px' }}>Requested</div>
        <div style={{ fontSize: '14px', color: '#C4B5FD', fontWeight: '600' }}>
          {new Date(boost.requestedAt).toLocaleDateString()}
        </div>
      </div>
    </div>
  </div>
);

const SuccessBanner = ({ onDismiss }) => (
  <div style={{
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    background: 'rgba(16,185,129,0.1)', border: '1px solid rgba(16,185,129,0.3)',
    borderRadius: '10px', padding: '14px 18px', marginBottom: '24px',
  }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
      <CheckCircle size={18} style={{ color: '#10B981' }} />
      <span style={{ fontSize: '14px', color: '#10B981', fontWeight: '600' }}>
        Payment confirmed — your Discovery Boost is now active!
      </span>
    </div>
    <button onClick={onDismiss} style={{ background: 'none', border: 'none', color: '#10B981', cursor: 'pointer' }}>
      <X size={16} />
    </button>
  </div>
);

const DiscoveryBoostPanel = () => {
  const [boost, setBoost] = useState(undefined);
  const [loading, setLoading] = useState(true);
  const [showSuccess, setShowSuccess] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('success') === 'true') {
      setShowSuccess(true);
      window.history.replaceState({}, '', window.location.pathname);
    }

    creatorAPI.getBoostStatus()
      .then(d => setBoost(d.boost || null))
      .catch(() => setBoost(null))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '80px' }}>
        <Loader size={24} style={{ animation: 'spin 1s linear infinite', color: '#8B5CF6' }} />
        <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  const showForm = !boost || boost.status === 'cancelled' || boost.status === 'expired' || boost.status === 'pending_payment';

  return (
    <div style={{ maxWidth: '720px', margin: '0 auto' }}>
      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>

      {showSuccess && <SuccessBanner onDismiss={() => setShowSuccess(false)} />}

      {showForm && <RequestForm />}
      {boost?.status === 'active' && (
        <ActiveBoostCard boost={boost} onUpdateCarousel={() => creatorAPI.getBoostStatus().then(d => setBoost(d.boost))} />
      )}
    </div>
  );
};

export default DiscoveryBoostPanel;
